import { z } from 'zod';
import { isBoolean } from 'lodash';

const anyObject = z.object({}).passthrough();
const anyArray = z.array(z.unknown());

export const countBuilderErrors = (errors: unknown): number => {
  if (isBoolean(errors)) {
    return errors ? 1 : 0;
  }

  const maybeArray = anyArray.safeParse(errors);
  if (maybeArray.success) {
    return maybeArray.data.reduce<number>(
      (acc, error) => acc + countBuilderErrors(error),
      0,
    );
  }

  const maybeObject = anyObject.safeParse(errors);
  if (maybeObject.success) {
    return countBuilderErrors(Object.values(maybeObject.data));
  }

  return 0;
};

/**
 * Counts the number for errors from React Hook Form FieldError structure. FieldError
 * looks like this:
 * {
 *   "conditions": [
 *     {
 *       "predicates": [
 *         {
 *           "relOp": {
 *             "message": "Value is required",
 *             "type": "custom",
 *             "ref": {}
 *           }
 *         }
 *       ]
 *     }
 *   ]
 * }
 * or like this:
 * [
 *   {
 *     "predicates": [
 *       {
 *         "operand": {
 *           "fromOperand": {
 *             "message": "Value is required",
 *             "type": "custom",
 *             "ref": {
 *               "name": "conditions.0.predicates.0.operand.fromOperand"
 *             }
 *           },
 *           "toOperand": {
 *             "message": "Value is required",
 *             "type": "custom",
 *             "ref": {
 *               "name": "conditions.0.predicates.0.operand.toOperand"
 *             }
 *           }
 *         }
 *       }
 *     ]
 *   }
 * ]
 * Uses heuristics to "guess" if certain object "look" like error definition
 * via rhfError Zod schema. Otherwise, continues recursively to scan the
 * form errors which mimic the form schema, but instead of fields, there are
 * error objects
 */
const rhfError = z.object({
  type: z.string(),
  message: z.string(),
  ref: z.object({}), // just presence of ref key with object value
});
export const countReactHookFormErrors = (errors: unknown): number => {
  const maybeRhfError = rhfError.safeParse(errors);
  if (maybeRhfError.success) {
    return 1;
  }

  const maybeArray = anyArray.safeParse(errors);
  if (maybeArray.success) {
    return maybeArray.data.reduce<number>(
      (acc, error) => acc + countReactHookFormErrors(error),
      0,
    );
  }

  const maybeObject = anyObject.safeParse(errors);
  if (maybeObject.success) {
    return countReactHookFormErrors(Object.values(maybeObject.data));
  }

  return 0;
};

export const countZodErrors = (errors: unknown): number => {
  if (typeof errors === 'string') {
    return 1;
  }

  const maybeArray = anyArray.safeParse(errors);
  if (maybeArray.success) {
    return maybeArray.data.reduce<number>(
      (acc, error) => acc + countZodErrors(error),
      0,
    );
  }

  const maybeObject = anyObject.safeParse(errors);
  if (maybeObject.success) {
    return countZodErrors(Object.values(maybeObject.data));
  }

  return 0;
};
